import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Nav, Card, Alert, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import SavedReportsList from '../components/customReports/SavedReportsList';
import ReportBuilder from '../components/customReports/ReportBuilder';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { useAuth } from '../hooks/useAuth';

const CustomReports: React.FC = () => {
  const { t } = useTranslation(['pages', 'common']);
  const [activeTab, setActiveTab] = useState<'list' | 'builder'>('list');
  const [editingReportId, setEditingReportId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'edit' | 'preview'>('edit');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { canViewCustomReports, canCreateCustomReports } = useRolePermissions();
  const { userRolePermissionsLoading } = useAuth();

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = t('customReports.unsavedChanges');
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, t]);

  const handleEditReport = (reportId: number, mode: 'edit' | 'preview' = 'edit') => {
    setEditingReportId(reportId);
    setViewMode(mode);
    setActiveTab('builder');
    setHasUnsavedChanges(false);
  };

  const handleCreateNew = () => {
    setEditingReportId(null);
    setViewMode('edit');
    setActiveTab('builder');
    setHasUnsavedChanges(false);
  };

  const handleTabSelect = (k: string | null) => {
    if (activeTab === 'builder' && hasUnsavedChanges) {
      if (window.confirm(t('customReports.unsavedChanges'))) {
        setActiveTab(k as any);
        setHasUnsavedChanges(false);
      }
    } else {
      setActiveTab(k as any);
    }
  };

  if (userRolePermissionsLoading) {
    return (
      <Layout title={t('customReports.title')}>
        <Container fluid className="py-4">
          <Row className="mb-4">
            <Col>
              <h2 className="fw-bold mb-0">{t('customReports.title')}</h2>
            </Col>
          </Row>
          <div className="d-flex flex-column align-items-center justify-content-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted mb-0">{t('customReports.loading')}</p>
          </div>
        </Container>
      </Layout>
    );
  }

  if (!canViewCustomReports) {
    return (
      <Layout title={t('customReports.title')}>
        <Container fluid className="py-4">
          <Row className="mb-4">
            <Col>
              <h2 className="fw-bold mb-0">{t('customReports.title')}</h2>
            </Col>
          </Row>
          <Alert variant="danger">{t('customReports.noAccess')}</Alert>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={t('customReports.title')}>
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2 className="fw-bold mb-0">{t('customReports.title')}</h2>
          </Col>
        </Row>

        <Card className="shadow-sm">
          <Card.Header className="bg-white border-0 pb-0 pt-3">
            <Nav
              variant="underline"
              className="flex-nowrap gap-3"
              activeKey={activeTab}
              onSelect={handleTabSelect}
            >
              <Nav.Item>
                <Nav.Link eventKey="list" className="px-1 py-2 mb-0">
                  {t('customReports.myReports')}
                </Nav.Link>
              </Nav.Item>
              {(canCreateCustomReports || editingReportId !== null) && (
                <Nav.Item>
                  <Nav.Link eventKey="builder" className="px-1 py-2 mb-0">
                    {editingReportId
                      ? viewMode === 'preview'
                        ? t('buttons.view', { ns: 'common' })
                        : t('buttons.edit', { ns: 'common' })
                      : t('customReports.builder')}
                  </Nav.Link>
                </Nav.Item>
              )}
            </Nav>
          </Card.Header>
          <Card.Body className="pt-3">
            {activeTab === 'list' ? (
              <SavedReportsList onEdit={handleEditReport} onCreateNew={handleCreateNew} />
            ) : (
              <ReportBuilder 
                reportId={editingReportId} 
                viewMode={viewMode}
                onSaveSuccess={() => {
                  setActiveTab('list');
                  setHasUnsavedChanges(false);
                }} 
                onCancel={() => {
                  if (!hasUnsavedChanges || window.confirm(t('customReports.unsavedChanges'))) {
                    setActiveTab('list');
                    setHasUnsavedChanges(false);
                  }
                }}
                onChange={() => setHasUnsavedChanges(true)}
              />
            )}
          </Card.Body>
        </Card>
      </Container>
    </Layout>
  );
};

export default CustomReports;
